/**
 * Browser plumbing for handling a PDF the app has fetched: naming it, saving
 * it, and printing it on its own.
 *
 * Every function here is one ordering or MIME detail away from failing
 * silently — a blob typed as octet-stream downloads instead of rendering, an
 * iframe attached before its src prints a blank page — so they live together,
 * documented, rather than inline in a component.
 */

import { logSecretly } from "./logSecretly";

export const PDF_MIME_TYPE = "application/pdf";

/** How long to keep an object URL alive after handing it to the browser. */
const OBJECT_URL_GRACE_MS = 1000;

/**
 * Reads a response as a PDF blob.
 *
 * A blob inherits the response's Content-Type, and storage commonly serves
 * these as application/octet-stream — which the browser can't display, so
 * pointing a viewer at it downloads the file instead of rendering it.
 * Restating the type is what makes the bytes previewable.
 */
export const asPdfBlob = async (response: Response): Promise<Blob> =>
  new Blob([await response.arrayBuffer()], { type: PDF_MIME_TYPE });

/**
 * A readable file name taken from a URL's last path segment, which storage
 * URLs generally already provide. Falls back when the URL is unparseable or
 * doesn't end in .pdf.
 */
export const fileNameFromUrl = (url: string, fallback: string): string => {
  try {
    const path = new URL(url).pathname;
    const name = decodeURIComponent(path.split("/").pop() || "");
    return name.toLowerCase().endsWith(".pdf") ? name : fallback;
  } catch {
    return fallback;
  }
};

/** Prompts the browser to save a blob under the given name. */
export const saveBlobAs = (blob: Blob, fileName: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking synchronously cancels the download still being written out.
  setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_GRACE_MS);
};

/**
 * Prints a PDF on its own, without the surrounding page.
 *
 * window.print() would print whatever is on screen, so the file is loaded into
 * a throwaway frame and that frame is printed instead.
 *
 * @param onFailure called if the frame can't be printed, so the caller can
 *                  fall back (e.g. open the file in the browser's own viewer).
 * @returns a cleanup function. It runs itself when the print dialog closes;
 *          call it if the caller goes away first, or a dialog left open would
 *          strand the frame and its object URL.
 */
export const printPdfBlob = (blob: Blob, onFailure?: () => void): (() => void) => {
  const objectUrl = URL.createObjectURL(blob);
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  // Parked off-screen rather than display:none — Chrome won't start its PDF
  // viewer in a frame with no box, and then there is nothing to print.
  frame.style.cssText =
    "position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;";

  // Safe to run twice: both paths below may reach it.
  const cleanup = () => {
    frame.remove();
    URL.revokeObjectURL(objectUrl);
  };

  frame.onload = () => {
    try {
      const frameWindow = frame.contentWindow;
      if (!frameWindow) throw new Error("Print frame has no window");
      // Reachable only because the blob URL is same-origin to this page.
      frameWindow.addEventListener("afterprint", cleanup, { once: true });
      frameWindow.focus();
      frameWindow.print();
    } catch (err) {
      logSecretly("Could not print the PDF:", err);
      cleanup();
      onFailure?.();
    }
  };
  frame.onerror = cleanup;

  // src first, then attach: appending an iframe with no src loads about:blank
  // and fires onload for it, which would print an empty page.
  frame.src = objectUrl;
  document.body.appendChild(frame);

  return cleanup;
};
